import { Injectable } from '@angular/core';
import { ArticleImportSettings } from '../ArticleImportSettings';
import {
	AIMessage,
	Article,
	ArticleType,
	AudioArticle,
	BackendTag,
	DocArticle,
	ExternalItem,
	ImageArticle,
	Imagesizes,
	LoginInfo,
	PDFArticle,
	PluginType,
	PublicDataServiceProvider,
	TagRequest,
	TextArticle,
	VideoArticle,
} from '@meta-press/shared-plugins';
import { LoginService } from '../login-service/login.service';
import { EditorGameDefinition } from '../EditorGameDefinition';
import { NotificationService } from '../notification-service/notification.service';

import {
	BehaviorSubject,
	catchError,
	forkJoin,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	tap,
	throwError,
} from 'rxjs';
import { EditorGameDefinitionService } from '../services/editor-game-definition.service';
import { BackendService } from '../backend-service/backend.service';
import { isObj } from '@anthropic-ai/sdk/core.mjs';

export enum SourceMaterialState {
	NotReady = 'NotReady',
	Preparing = 'Preparing',
	Ready = 'Ready',
	Error = 'Error',
}

/** Service for managing and providing source material, like articles and media */
@Injectable({
	providedIn: 'root',
})
export class SourceMaterialService {
	private selectedArticles: Article[] = [];

	protected articleSettings: Map<number, ArticleImportSettings> = new Map<
		number,
		ArticleImportSettings
	>();

	private initialized = false;

	// Tag management
	private tags: BackendTag[] = [];
	private tagsInitialized = false;

	// If this is true, no plugin can use generation services until the source material is prepared.

	public get SourceMaterialState(): SourceMaterialState {
		return this._isPreparingSourceMaterial;
	}

	public set SourceMaterialState(value: SourceMaterialState) {
		this._isPreparingSourceMaterial = value;
		this._sourceMaterialStateSubject.next(value);
	}

	private _isPreparingSourceMaterial: SourceMaterialState =
		SourceMaterialState.Ready;

	// BehaviorSubject to emit state changes
	private _sourceMaterialStateSubject =
		new BehaviorSubject<SourceMaterialState>(SourceMaterialState.Ready);

	/**
	 * Observable that emits when SourceMaterialState changes
	 */
	public get sourceMaterialState$(): Observable<SourceMaterialState> {
		return this._sourceMaterialStateSubject.asObservable();
	}

	// BehaviorSubject to emit articles changes
	private _articlesSubject = new BehaviorSubject<Article[]>([]);

	/**
	 * Observable that emits when articles array changes
	 */
	public get articles$(): Observable<Article[]> {
		return this._articlesSubject.asObservable();
	}

	public get Initialized(): boolean {
		return this.initialized;
	}

	private objectURLs: Map<number, string> = new Map<number, string>();

	// Track the image size that has been fetched for each image article
	private imageSizes: Map<number, Imagesizes> = new Map<number, Imagesizes>();

	// Track failed fetch attempts to prevent repeated requests
	// Format: "shareUuid:id" for server fetches, just "id" for local fetches
	private failedFetches: Set<string> = new Set<string>();

	// Track pending image fetch requests to prevent duplicate HTTP calls
	// Key: articleId, Value: { size, observable }
	// When a larger size is requested, it will replace smaller pending requests
	private pendingImageFetches: Map<
		number,
		{ size: Imagesizes; observable: Observable<string | undefined> }
	> = new Map<
		number,
		{ size: Imagesizes; observable: Observable<string | undefined> }
	>();

	public get ObjectURLs(): Map<number, string> {
		return this.objectURLs;
	}

	constructor(
		private loginService: LoginService,
		private backendService: BackendService,
		private publicDataServiceProvider: PublicDataServiceProvider,
		private editorGameDefinitionService: EditorGameDefinitionService,
		private notificationService: NotificationService,
	) {
		// Listen to user changes and load articles accordingly
		this.loginService.OnLogin.subscribe({
			next: (success: boolean) => {
				if (success) {
					// Load both articles and tags
					forkJoin([
						this.RefreshArticles(),
						this.RefreshTags(),
					]).subscribe({
						next: ([articles, tags]) => {
							console.log(
								'Articles and tags initialized in constructor:',
								this.articles,
								this.tags,
							);
						},
						error: (error) => {
							console.error(
								'Error loading articles or tags:',
								error,
							);
						},
					});
				}
				// this.SetArticles(user.articles);
			},
			error: (error) => {
				console.error('Error during login:', error);
				this.initialized = true; // Set initialized to true even on error
				this.tagsInitialized = true;
			},
		});

		// Provide public backend service data access to CreateObjectURL's when in editor so it doesn't have to download them from server. In shared plugins, files are always downloaded by access permissions.
		this.publicDataServiceProvider.setDynamicGetter(
			(fileId: number, size: Imagesizes) => {
				return this.GetURLAsObject(fileId, size);
			},
		);
	}

	public GetURLAsObject(
		id: number,
		size: Imagesizes = Imagesizes.Thumb,
	): Observable<string | undefined> {
		if (this.objectURLs.has(id)) {
			// Only return if there's an object for the particular image size
			if (this.imageSizes.has(id) && this.imageSizes.get(id) == size) {
				return of(this.objectURLs.get(id));
			}
		}

		// Check if we've already tried and failed to fetch this file
		if (this.failedFetches.has(String(id))) {
			return of(undefined);
		}

		// If not found, see if it's in the source material and if yes, create it.
		const article = this.GetArticleById(id);
		if (article) {
			// If article is found, create object URL for it
			// For images, use the resized image endpoint with size tracking
			if (article.type === 'image') {
				return this.fetchImageWithSize(id, article, size);
			}

			// For non-image files, fetch file from editor backend
			return this.backendService.GetSingleFile(id).pipe(
				tap(() => {
					console.log(
						'Fetched file from editor backend for article with id: ' +
							id,
					);
				}),
				switchMap((data: Blob) => {
					// Convert to file object
					const file = new File([data], article.title, {
						type: data.type,
					});
					console.log('Converted article to a file: ', article, file);
					// Create object URL
					const objectURL = URL.createObjectURL(file);
					this.objectURLs.set(id, objectURL);
					return of(objectURL);
				}),
				catchError((error) => {
					console.log(
						'Local file not found for article with id: ' +
							id +
							', attempting server fallback',
					);
					// Mark as failed locally
					this.failedFetches.add(String(id));
					// If local fetch fails, try to get it from server via public backend service
					return this.fetchFromServerFallback(id, article);
				}),
			);
		} else {
			console.warn(
				'Article with id ' + id + ' not found in source material.',
			);
			// Mark as failed since article doesn't exist
			this.failedFetches.add(String(id));
			return of(undefined); // If article not found, return undefined
		}
	}

	/**
	 * Request an image at a specific size, potentially upgrading from a smaller cached version
	 * @param articleId The article ID
	 * @param size The desired image size
	 * @returns Observable of the object URL for the image at the requested size
	 */
	public GetImageAtSize(
		articleId: number,
		size: Imagesizes,
	): Observable<string | undefined> {
		const article = this.GetArticleById(articleId);
		if (!article) {
			console.warn(
				`Article with id ${articleId} not found in source material.`,
			);
			return of(undefined);
		}

		if (article.type !== 'image') {
			console.warn(
				`Article ${articleId} is not an image article. Type: ${article.type}`,
			);
			return of(undefined);
		}

		return this.fetchImageWithSize(articleId, article, size);
	}

	/**
	 * Fetches an image at a specific size, handling upgrades if needed
	 * @param articleId The article ID
	 * @param article The article object
	 * @param requestedSize The requested image size
	 * @returns Observable of the object URL
	 */
	private fetchImageWithSize(
		articleId: number,
		article: Article,
		requestedSize: Imagesizes,
	): Observable<string | undefined> {
		const currentSize = this.imageSizes.get(articleId);

		// Check if we already have this image at the requested size or larger
		// Note: Smaller enum values = larger sizes (Xl=0, Lg=1, Md=2, Sm=3, Thumb=4)
		if (currentSize !== undefined && currentSize <= requestedSize) {
			// We already have a suitable size cached
			// console.log(
			// 	`Using existing cached image for article ${articleId} (${article.title}) at size ${Imagesizes[currentSize]}`,
			// );
			return of(this.objectURLs.get(articleId));
		}

		// Check if there's already a pending request for this article
		const pendingRequest = this.pendingImageFetches.get(articleId);
		if (pendingRequest) {
			// If pending request is for equal or larger size, return it
			// (smaller enum value = larger size, so we check <=)
			if (pendingRequest.size <= requestedSize) {
				// console.log(
				// 	`Reusing pending request for article ${articleId} (${article.title}) at size ${Imagesizes[pendingRequest.size]} (requested: ${Imagesizes[requestedSize]})`,
				// );
				return pendingRequest.observable;
			}
			// If pending request is for smaller size, we need to fetch the larger size
			// Don't cancel the smaller request, just create a new one for the larger size
			// console.log(
			// 	`Upgrading pending request for article ${articleId} from ${Imagesizes[pendingRequest.size]} to ${Imagesizes[requestedSize]}`,
			// );
		}

		// Need to fetch or upgrade the image
		const isUpgrade = currentSize !== undefined;
		const oldSize = currentSize;

		// Create the fetch observable with shareReplay to prevent duplicate requests
		const fetchObservable = this.backendService
			.GetResizedImage(articleId, requestedSize)
			.pipe(
				tap(() => {
					if (isUpgrade) {
						console.log(
							`Upgrading image for article ${articleId} (${article.title}) from ${Imagesizes[oldSize!]} to ${Imagesizes[requestedSize]}`,
						);
						// Revoke the old object URL to free memory
						const oldUrl = this.objectURLs.get(articleId);
						if (oldUrl) {
							URL.revokeObjectURL(oldUrl);
						}
					}
				}),
				map((data: Blob) => {
					// Convert to file object
					const file = new File([data], article.title, {
						type: data.type,
					});

					// Create object URL
					const objectURL = URL.createObjectURL(file);
					this.objectURLs.set(articleId, objectURL);
					this.imageSizes.set(articleId, requestedSize);

					// Remove from pending fetches on success
					this.pendingImageFetches.delete(articleId);

					return objectURL;
				}),
				catchError((error) => {
					console.error(
						`Error fetching image at size ${Imagesizes[requestedSize]} for article ${articleId} (${article.title}):`,
						error,
					);
					// Remove from pending fetches on error
					this.pendingImageFetches.delete(articleId);
					// Mark as failed locally
					this.failedFetches.add(String(articleId));
					// If local fetch fails, try to get it from server via public backend service
					return this.fetchFromServerFallback(articleId, article);
				}),
				// shareReplay(1) ensures multiple subscribers get the same result without triggering duplicate HTTP requests
				shareReplay(1),
			);

		// Store the pending request
		this.pendingImageFetches.set(articleId, {
			size: requestedSize,
			observable: fetchObservable,
		});

		return fetchObservable;
	}

	/**
	 * Fallback method to fetch image from server when local fetch fails
	 * Uses the public backend service to fetch images from shared experiences
	 */
	private fetchFromServerFallback(
		id: number,
		article: Article,
	): Observable<string | undefined> {
		// Only attempt server fallback for image articles
		if (article.type !== 'image') {
			console.warn(
				`Server fallback only supported for images. Article ${id} is type: ${article.type}`,
			);
			return of(undefined);
		}

		// Get the default share UUID from public data service provider
		const shareUuid = this.publicDataServiceProvider.GetDefaultShareUuid();
		if (!shareUuid) {
			console.warn(
				`No default share UUID available for server fallback of image ${id}`,
			);
			return of(undefined);
		}

		// Check if we've already tried and failed with this shareUuid
		const failedKey = `${shareUuid}:${id}`;
		if (this.failedFetches.has(failedKey)) {
			return of(undefined);
		}

		// Use the PublicBackendService to fetch the image from server
		return this.publicDataServiceProvider.GetURLAsObject(id, 'image').pipe(
			switchMap((serverUrl: string | undefined) => {
				if (serverUrl) {
					// Server returned a URL, cache it locally and return it
					this.objectURLs.set(id, serverUrl);
					console.log(
						`Successfully fetched image ${id} from server fallback`,
					);
					return of(serverUrl);
				} else {
					console.warn(
						`Server fallback failed for image ${id} - no URL returned`,
					);
					// Mark as failed
					const failedKey = `${shareUuid}:${id}`;
					this.failedFetches.add(failedKey);
					this.failedFetches.add(String(id));
					// Show notification to user about missing file
					this.notificationService.ShowToast(
						`File missing from server: ${article.title} (ID: ${id})`,
						'warning',
					);
					return of(undefined);
				}
			}),
			catchError((error) => {
				console.error(`Server fallback failed for image ${id}:`, error);
				// Mark as failed
				const failedKey = `${shareUuid}:${id}`;
				this.failedFetches.add(failedKey);
				this.failedFetches.add(String(id));
				// Show notification to user if it's a 404 or other error
				if (error.status === 404) {
					this.notificationService.ShowToast(
						`File not found on server: ${article.title} (ID: ${id})`,
						'danger',
					);
				} else {
					this.notificationService.ShowToast(
						`Failed to fetch file: ${article.title} (ID: ${id})`,
						'warning',
					);
				}
				return of(undefined);
			}),
		);
	}

	public RefreshArticles(): Observable<Article[]> {
		this.initialized = false;

		this.selectedArticles = [];
		this.articles = [];

		// Clear images
		this.objectURLs.forEach((url) => {
			URL.revokeObjectURL(url);
		});

		this.objectURLs.clear();

		// Also clear the failed fetches cache as new files might be available
		this.failedFetches.clear();

		// Clear pending image fetches as they're no longer valid
		this.pendingImageFetches.clear();

		return this.backendService.GetFiles().pipe(
			tap((externalItems: ExternalItem[]) => {
				// console.log('Fetched list of files:', externalItems);
			}),
			// Convert to articles
			map((externalItems: ExternalItem[]) => {
				const articles: Article[] = [];

				externalItems.forEach((item: ExternalItem) => {
					try {
						const metadata = JSON.parse(item.meta);
						let article = metadata;

						// Legacy support
						if (metadata.articleType) {
							article = metadata.article;
						}

						switch (article.type) {
							case ArticleType.Text: {
								const newArticle = article as TextArticle;
								newArticle.id = item.id;
								newArticle.dateCreated = item.date_added;
								newArticle.tags.push(...item.tags);
								articles.push(newArticle);
								break;
							}

							case ArticleType.PDF: {
								const newArticle = article as PDFArticle;
								newArticle.id = item.id;
								newArticle.dateCreated = item.date_added;
								newArticle.tags.push(...item.tags);
								articles.push(newArticle);
								break;
							}

							case ArticleType.Image: {
								const newArticle = article as ImageArticle;
								newArticle.id = item.id;
								newArticle.dateCreated = item.date_added;
								newArticle.tags.push(...item.tags);
								// if (metadata.imageMetadata)
								// 	newArticle.metadata =
								// 		metadata.imageMetadata;
								articles.push(newArticle);
								break;
							}

							case ArticleType.Audio: {
								const newArticle = article as AudioArticle;
								newArticle.id = item.id;
								newArticle.dateCreated = item.date_added;
								newArticle.tags.push(...item.tags);
								articles.push(newArticle);
								break;
							}

							default: {
								console.error(
									'Found an article with no type - ignoring: ' +
										metadata,
								);
							}
						}
					} catch (error) {
						console.error(
							'Error parsing metadata for item:',
							item,
							error,
						);
					}
				});

				console.log(
					'Fetched list of files: ' +
						externalItems +
						' and converted to articles:',
					articles,
				);

				return articles;
			}),
			// Set articles, initialized state, and emit immediately to unblock the UI
			tap((articles: Article[]) => {
				// Set articles array and initialized state immediately
				this.articles = articles;
				this.initialized = true;
				// Emit the updated articles array so UI can render
				this._articlesSubject.next([...this.articles]);
				console.log(
					'Article library refreshed - metadata loaded. Loading images/audio in background...',
				);

				// Fetch images/audio in background without blocking
				articles.forEach((article: Article) => {
					if (article.type === 'image') {
						// For images, use GetResizedImage with Thumb size
						this.backendService
							.GetResizedImage(article.id, Imagesizes.Thumb)
							.pipe(
								map((data: Blob) => {
									// Convert to file object
									const file = new File(
										[data],
										article.title,
										{
											type: data.type,
										},
									);

									// Create file object
									const objectURL = URL.createObjectURL(file);
									this.objectURLs.set(article.id, objectURL);
									this.imageSizes.set(
										article.id,
										Imagesizes.Thumb,
									);

									return article;
								}),
								catchError((error) => {
									console.error(
										'Error fetching image for article:',
										article,
										error,
									);

									return of(article);
								}),
							)
							.subscribe();
					} else if (article.type === 'audio') {
						// For audio, use GetSingleFile
						this.backendService
							.GetSingleFile(article.id)
							.pipe(
								tap(() => {
									console.log(
										`Fetched audio for article ${article.id} (${article.title})`,
									);
								}),
								map((data: Blob) => {
									// Convert to file object
									const file = new File(
										[data],
										article.title,
										{
											type: data.type,
										},
									);

									// Create file object
									const objectURL = URL.createObjectURL(file);
									this.objectURLs.set(article.id, objectURL);

									return article;
								}),
								catchError((error) => {
									console.error(
										'Error fetching audio for article:',
										article,
										error,
									);

									return of(article);
								}),
							)
							.subscribe();
					}
				});
			}),
			catchError((error) => {
				console.error('Error fetching articles:', error);
				this.initialized = true;
				// Emit empty array on error
				this._articlesSubject.next([]);
				return of([]);
			}),
		);
	}

	DeleteArticle(article: Article): Observable<boolean> {
		return this.backendService.DeleteFileFromServer(article.id).pipe(
			catchError((error) => {
				console.error('Error deleting article:', error);
				return throwError(() => {
					new Error('Error deleting article: ' + error.message);
				});
			}),
			map((_) => {
				this.articles = this.articles.filter(
					(a) => a.id !== article.id,
				);
				// Also remove from selected articles if present
				this.selectedArticles = this.selectedArticles.filter(
					(a) => a.id !== article.id,
				);
				// Emit the updated articles array
				this._articlesSubject.next([...this.articles]);
				return true;
			}),
		);
	}

	DeleteArticles(articles: Article[]): Observable<Article[]> {
		const deleteObservables: Observable<Article>[] = [];

		articles.forEach((article: Article) => {
			deleteObservables.push(
				this.DeleteArticle(article).pipe(map(() => article)),
			);
		});

		return forkJoin(deleteObservables);
	}

	GetArticleById(articleId: number): Article | undefined {
		return this.articles.find((a) => a.id === articleId);
	}

	/**
	 * Filters article IDs to only include external items (image, audio, pdf) that need to be included in shares.
	 * This is used when creating shares to determine which external files need to be packaged with the experience.
	 * @param articleIds Array of article IDs to filter
	 * @returns Filtered array of article IDs containing only image, audio, and pdf types
	 */
	public filterExternalArticleIds(articleIds: number[]): number[] {
		return articleIds.filter((id) => {
			const article = this.GetArticleById(id);
			return (
				article &&
				(article.type === 'image' ||
					article.type === 'audio' ||
					article.type === 'pdf')
			);
		});
	}

	SelectAll(state: boolean) {
		if (state) {
			this.selectedArticles = this.articles;
		} else {
			this.selectedArticles = [];
		}
	}

	public IsSelected(value: Article): boolean {
		return this.selectedArticles.includes(value);
	}

	public ResetVectorStoreStates(): void {
		this.articles.forEach((article) => {
			article.vectorStorageState = 'not_added';
		});
	}

	public GetArticlesByMe(): Article[] {
		const user = this.loginService.GetCurrentUser();
		if (user) {
			// TODO: Filter by user owned articles, not pseudo "isMine"-property
			return this.articles.filter((article) => article.isMine);
		}
		return [];
	}

	public GetArticlesByOthers(): Article[] {
		const user = this.loginService.GetCurrentUser();
		if (user) {
			return this.articles.filter((article) => !article.isMine);
		}
		return [];
	}

	public SetArticleSettings(event: Map<number, ArticleImportSettings>): void {
		this.articleSettings = event;
	}

	public GetArticleSettings(id: number): ArticleImportSettings {
		if (!this.articleSettings.has(id)) {
			this.articleSettings.set(id, { allowRewrite: 'deny' });
		}

		return this.articleSettings.get(id) as ArticleImportSettings;
	}

	public GetHandlesForSelectedFiles(user: LoginInfo): number[] {
		const fileHandlesToInclude: number[] = [];

		this.selectedArticles.forEach((article) => {
			fileHandlesToInclude.push(article.id);
		});
		return fileHandlesToInclude;
	}

	public GetHandlesForFiles(user: LoginInfo, articles: Article[]): number[] {
		const fileHandlesToInclude: number[] = [];

		articles.forEach((article) => {
			fileHandlesToInclude.push(article.id);
		});
		return fileHandlesToInclude;
	}

	// public GetAllTags(): string[] {
	// 	const tags: string[] = [];
	// 	this.articles.forEach((article) => {
	// 		article.tags.forEach((tag) => {
	// 			if (!tags.includes(tag)) {
	// 				tags.push(tag);
	// 			}
	// 		});
	// 	});
	// 	return tags;
	// }
	public GetAllTagsOfSelectedArticles(): BackendTag[] {
		const tags: number[] = [];
		this.selectedArticles.forEach((article) => {
			article.tags.forEach((tagId) => {
				if (!tags.includes(tagId)) {
					tags.push(tagId);
				}
			});
		});
		return this.GetTagsAsDefinitions(tags);
	}

	public GetAllTagsOfArticleType(
		type: 'text' | 'pdf' | 'image' | 'audio' | 'video',
		sourceArticles: Article[],
	): BackendTag[] {
		const tags: number[] = [];
		sourceArticles.forEach((article) => {
			if (article.type !== type) return;
			article.tags.forEach((tagId) => {
				if (!tags.includes(tagId)) {
					tags.push(tagId);
				}
			});
		});
		return this.GetTagsAsDefinitions(tags);
	}

	public GetTagsAsDefinitions(tagIds: number[]): BackendTag[] {
		return this.tags.filter((tag) => tagIds.includes(tag.id));
	}

	public GetTagIdsByNames(tagNames: string[]): number[] {
		const tagIds: number[] = [];
		this.tags.forEach((tag) => {
			if (
				tagNames.find(
					(tagName) =>
						tagName.toLowerCase() == tag.name.toLowerCase(),
				)
			) {
				tagIds.push(tag.id);
			}
		});
		return tagIds;
	}

	async CreateArticlePrompts(
		selectedArticles: Article[] = this.SelectedArticles,
		shouldCache = false,
	): Promise<AIMessage[]> {
		const articlePrompts: AIMessage[] = [];

		// Process articles - convert articles to prompts
		for (let i = 0; i < this.SelectedArticles.length; i++) {
			const a: Article = this.SelectedArticles[i];
			let content = '';

			switch (a.type) {
				case 'text':
					{
						content = a.content;
					}

					break;
				case 'audio':
					{
						const audioArticle = a as AudioArticle;
						if (
							audioArticle.litterationWithWordsTimestamp !==
								undefined &&
							audioArticle.litterationWithWordsTimestamp.length >
								0
						) {
							content = audioArticle.litterationWithWordsTimestamp
								.map((item) => JSON.stringify(item))
								.join('\n');
						} else if (
							audioArticle.litterationWithSegmentsTimestamp !==
								undefined &&
							audioArticle.litterationWithSegmentsTimestamp
								.length > 0
						) {
							content =
								audioArticle.litterationWithSegmentsTimestamp
									.map((item) => JSON.stringify(item))
									.join('\n');
						} else {
							content = audioArticle.content;
						}
					}

					break;

				case 'video':
					{
						const videoArticle = a as VideoArticle;
						if (
							videoArticle.litterationWithWordsTimestamp !==
								undefined &&
							videoArticle.litterationWithWordsTimestamp.length >
								0
						) {
							content = videoArticle.litterationWithWordsTimestamp
								.map((item) => JSON.stringify(item))
								.join('\n');
						} else if (
							videoArticle.litterationWithSegmentsTimestamp !==
								undefined &&
							videoArticle.litterationWithSegmentsTimestamp
								.length > 0
						) {
							content =
								videoArticle.litterationWithSegmentsTimestamp
									.map((item) => JSON.stringify(item))
									.join('\n');
						} else {
							content = videoArticle.content;
						}
					}

					break;

				case 'pdf':
					{
						const pdfArticle = a as PDFArticle;

						let pdfBlob: Response | undefined = undefined;

						switch (pdfArticle.sourceType) {
							case 'local':
								{
									// Fetch the file
									const pdfURL = pdfArticle.localPath;

									if (pdfURL !== undefined) {
										pdfBlob = await fetch(pdfURL);
									} else {
										console.error(
											'Local path not found for PDF article',
										);
									}
								}
								break;

							case 'url':
								{
									// Fetch the file
									const pdfURL = pdfArticle.url;

									if (pdfURL !== undefined) {
										pdfBlob = await fetch(pdfURL);
									} else {
										console.error(
											'Local path not found for PDF article',
										);
									}
								}
								break;
						}

						if (pdfBlob !== undefined) {
							// Then convert the file to base64
							const arrayBuffer = await pdfBlob.arrayBuffer();

							const uint8Array = new Uint8Array(arrayBuffer);
							const binaryString = uint8Array.reduce(
								(str, byte) => str + String.fromCharCode(byte),
								'',
							);
							const pdfBase64 = btoa(binaryString);

							// Add to messages
							articlePrompts.push({
								role: 'user',
								type: 'document',
								content: pdfBase64,
							} as AIMessage);
						}
					}
					break;

				default: {
					console.error(
						'Unable to process article type: ' +
							a.type +
							-' not implemented.',
					);
				}
			}

			const settings = this.GetArticleSettings(a.id);

			let ruleString = '';
			switch (settings?.allowRewrite) {
				case 'deny':
					ruleString +=
						'<article rewrite="deny" type="' +
						a.type +
						'">' +
						a.content +
						'</article>';
					break;
				case 'allow-limited':
					ruleString +=
						'<article rewrite="allow-limited" type="' +
						a.type +
						'">' +
						a.content +
						'</article>';
					break;
				case 'allow-full':
					ruleString +=
						'<article rewrite="allow-full" type="' +
						a.type +
						'">' +
						a.content +
						'</article>';
					break;
			}

			articlePrompts.push({
				role: 'system',
				type: 'text',
				cache: shouldCache,
				content:
					`On tärkeää, että otat huomioon lähdemateriaalin "rewrite"-parametriin. Jos se on deny, et saa muuttaa lähdemateriaalian tekstiä yhtään. Tällöin saat vain lainata lähdetekstiä. Et saa muuttaa sitä. Jos "rewrite"-parametri on "allow-limited", voit muokata tekstiä tarvittaessa, jotta kokonaisuus on yhtenäinen. Jos "rewrite"-parametri on "allow-full", voit kirjoittaa täysin uutta opetustekstiä lähdemateriaalin pohjalta.

				Tässä lähdemateriaali:
				` + ruleString,
			});
		}

		return articlePrompts;
	}

	public GetGameTypeSettings(
		type: PluginType,
	): EditorGameDefinition | undefined {
		return this.editorGameDefinitionService.editorGameDefinitions.find(
			(g) => g.type === type,
		);
	}

	private articles: Article[] = [];
	public get Articles(): Article[] {
		return this.articles;
	}

	public SetArticles(articles: Article[]) {
		this.articles = [];
		const i = 0;

		this.articles.push(...articles);

		// Emit the updated articles array
		this._articlesSubject.next([...this.articles]);

		// this.articles.forEach((article: Article) => {
		// 			article.id = i++;
		// 		});
	}

	public AddArticles(articles: Article[]) {
		articles.forEach((article: Article) => {
			// Find first free id in articles
			let i = 0;
			while (this.articles.find((a) => a.id === i) !== undefined) {
				i++;
			}
			article.id = i;
			this.articles.push(article);
		});

		// Emit the updated articles array
		this._articlesSubject.next([...this.articles]);
	}

	public GetImagesAsString(articles: Article[]): string | undefined {
		const invaliedEntries: Article[] = [];

		// Helper function to check if metadata is truly empty
		const hasValidMetadata = (metadata: unknown): boolean => {
			if (!metadata) return false;

			// If it's a string, parse it first
			if (typeof metadata === 'string') {
				try {
					const parsed = JSON.parse(metadata);
					metadata = parsed;
				} catch {
					// If parsing fails and string is not empty, consider it valid
					return (metadata as string)?.length > 0;
				}
			}

			// If it's an object, check if it has any properties
			if (isObj(metadata)) {
				return (
					Object.keys(metadata as Record<string, unknown>).length > 0
				);
			}

			return false;
		};

		// Filter images and collect valid metadata entries
		const validEntries = articles
			.filter((article) => article.type == 'image')
			.map((article) => {
				const metadata = (article as ImageArticle).metadata;

				if (hasValidMetadata(metadata)) {
					return JSON.stringify({
						id: article.id,
						metadata: metadata,
					});
				} else {
					this.notificationService.createNotification(
						`${article.title} sisältää tyhjää metadataa. Kuvaa ei voi käyttää lähdemateriaalina.`,
					);
					return null;
				}
			})
			.filter((entry) => entry !== null);

		// If no valid entries found, return undefined
		if (validEntries.length === 0) {
			return undefined;
		}

		// Return formatted string with all valid entries
		return (
			'Tässä saatavilla olevat kuvat. Jos haluat viitata kuviin (esim. jos täytät kuvakenttää, kuten imageId tai backgroundId tai muu kuvan id:seen liittyvä kenttä), tämän datan id:t ovat kuvien id:itä, joihin voit viitata: ' +
			validEntries.join('\n')
		);
	}

	public SelectByTags(tags: number[], sourceArticles: Article[]): Article[] {
		this.ClearSelection();

		if (tags.length === 0) {
			this.selectedArticles = [];
			return [];
		}

		const articles = sourceArticles.filter((article) => {
			return article.HasAnyTag(tags);
		});

		this.selectedArticles = articles;

		return articles;
	}

	public get FirstSelectedArticle(): Article | undefined {
		if (this.selectedArticles.length > 0) {
			return this.selectedArticles[0];
		}

		return undefined;
	}

	public get SelectedArticles(): Article[] {
		return this.selectedArticles;
	}

	public GetArticlesOfType(
		type: ('video' | 'audio' | 'pdf' | 'doc' | 'text' | 'image')[],
	): Article[] {
		return this.articles.filter((a) => type.includes(a.type));
	}

	public GetSelectedArticlesOfType(
		type: ('video' | 'audio' | 'pdf' | 'doc' | 'text' | 'image')[],
	): Article[] {
		return this.selectedArticles.filter((a) => type.includes(a.type));
	}

	public UpdateArticle(article: Article) {
		const index = this.articles.findIndex((a) => a.id === article.id);
		if (index !== -1) {
			this.articles[index] = article;
		}
	}

	public ClearSelection() {
		this.selectedArticles = [];
	}

	public Select(
		values: Article[] | undefined,
		removePreviousSelections = false,
		allowToggle = true,
	): void {
		if (values !== undefined) {
			// If solo
			if (removePreviousSelections) {
				this.selectedArticles = values;
				// // If something else than all or none are selected, select all
				// const allOrNoneSelected =
				// 	values.every((a) => this.selectedArticles.includes(a)) ||
				// 	this.selectedArticles.find((a) => values.includes(a)) ==
				// 		undefined;

				// // If all or none are selected, select all provided articles
				// if (!allOrNoneSelected) {
				// 	this.selectedArticles = values;
				// } else {
				// 	// If all or none are selected, flip selection
				// 	values.forEach((value) => {
				// 		if (!this.selectedArticles.includes(value)) {
				// 			// If not there, add only it
				// 			this.selectedArticles.push(value);
				// 		} else  {
				// 			// If already there, remove
				// 			this.selectedArticles = this.selectedArticles.filter(
				// 				(a) => a !== value,
				// 			);
				// 		}
				// 	});
				// }
			} else {
				// Not removing previous selections - toggling instead.
				values.forEach((value) => {
					if (!this.selectedArticles.includes(value)) {
						// If not there, add only it
						this.selectedArticles.push(value);
					} else if (allowToggle) {
						// If already there, remove
						this.selectedArticles = this.selectedArticles.filter(
							(a) => a !== value,
						);
					}
				});
			}
		} else {
			// If undefined, clear all
			this.selectedArticles = [];
		}
	}

	// ======== TAG MANAGEMENT METHODS ========

	public get Tags(): BackendTag[] {
		return this.tags;
	}

	public get TagsInitialized(): boolean {
		return this.tagsInitialized;
	}

	public RefreshTags(): Observable<BackendTag[]> {
		this.tagsInitialized = false;

		return this.backendService.GetTags().pipe(
			tap((tags: BackendTag[]) => {
				console.log('Fetched tags:', tags);
				this.tags = tags;
				this.tagsInitialized = true;
			}),
			catchError((error) => {
				console.error('Error fetching tags:', error);
				this.tagsInitialized = true;
				this.tags = [];
				return of([]);
			}),
		);
	}

	public AddTag(tagRequest: TagRequest): Observable<BackendTag> {
		return this.backendService.AddNewTag(tagRequest).pipe(
			switchMap((tagId: number) => {
				// After creating the tag, get the full tag object
				return this.backendService.GetTag(tagId);
			}),
			tap((newTag: BackendTag) => {
				// Add to local tags list
				this.tags.push(newTag);
				console.log('Tag added to local list:', newTag);
			}),
			catchError((error) => {
				console.error('Error adding tag:', error);
				return throwError(() => error);
			}),
		);
	}

	public UpdateTag(
		tagId: number,
		tagRequest: TagRequest,
	): Observable<BackendTag> {
		return this.backendService.UpdateTag(tagId, tagRequest).pipe(
			switchMap(() => {
				// After updating the tag, get the updated tag object
				return this.backendService.GetTag(tagId);
			}),
			tap((updatedTag: BackendTag) => {
				// Update in local tags list
				const index = this.tags.findIndex((t) => t.id === tagId);
				if (index !== -1) {
					this.tags[index] = updatedTag;
					console.log('Tag updated in local list:', updatedTag);
				}
			}),
			catchError((error) => {
				console.error('Error updating tag:', error);
				return throwError(() => error);
			}),
		);
	}

	public DeleteTag(tagId: number): Observable<boolean> {
		return this.backendService.DeleteTag(tagId).pipe(
			tap(() => {
				// Remove from local tags list
				this.tags = this.tags.filter((t) => t.id !== tagId);
				console.log('Tag removed from local list:', tagId);
			}),
			map(() => true),
			catchError((error) => {
				console.error('Error deleting tag:', error);
				return throwError(() => error);
			}),
		);
	}

	public AddTagToArticle(tagId: number, articleId: number): Observable<any> {
		return this.backendService.AddTagToArticle(tagId, articleId).pipe(
			tap(() => {
				// Find the tag and article and update locally if needed
				const tag = this.tags.find((t) => t.id === tagId);
				const article = this.articles.find((a) => a.id === articleId);
				if (tag && article && !article.tags.includes(tag.id)) {
					article.tags.push(tag.id);
					console.log(
						`Tag "${tag.name}" added to article "${article.title}"`,
					);
				}
			}),
			catchError((error) => {
				console.error('Error adding tag to article:', error);
				return throwError(() => error);
			}),
		);
	}

	public RemoveTagFromArticle(
		tagId: number,
		articleId: number,
	): Observable<any> {
		return this.backendService.RemoveTagFromArticle(tagId, articleId).pipe(
			tap(() => {
				// Find the tag and article and update locally if needed
				const tag = this.tags.find((t) => t.id === tagId);
				const article = this.articles.find((a) => a.id === articleId);
				if (tag && article) {
					article.tags = article.tags.filter((t) => t !== tag.id);
					console.log(
						`Tag "${tag.name}" removed from article "${article.title}"`,
					);
				}
			}),
			catchError((error) => {
				console.error('Error removing tag from article:', error);
				return throwError(() => error);
			}),
		);
	}

	public GetTagByName(name: string): BackendTag | undefined {
		return this.tags.find((t) => t.name === name);
	}

	public GetTagById(id: number): BackendTag | undefined {
		return this.tags.find((t) => t.id === id);
	}

	/**
	 * Clear the failed fetches cache - useful when new files might have been added
	 */
	public clearFailedFetchesCache(): void {
		this.failedFetches.clear();
	}

	/**
	 * Check if a file fetch has previously failed
	 */
	public hasFailedFetch(shareUuid: string | null, fileId: number): boolean {
		if (shareUuid) {
			const failedKey = `${shareUuid}:${fileId}`;
			return (
				this.failedFetches.has(failedKey) ||
				this.failedFetches.has(String(fileId))
			);
		}
		return this.failedFetches.has(String(fileId));
	}

	/**
	 * Add a new article from an ExternalItem (used after file upload)
	 * This method converts the ExternalItem to an Article and adds it to the local collection
	 * @param externalItem The ExternalItem returned from the backend after upload
	 * @returns The created Article, or undefined if parsing fails
	 */
	public addArticleFromExternalItem(
		externalItem: ExternalItem,
	): Article | undefined {
		try {
			const metadata = JSON.parse(externalItem.meta);
			let newArticle: Article | undefined;

			switch (metadata.type) {
				case ArticleType.Text: {
					newArticle = metadata as TextArticle;
					break;
				}
				case ArticleType.PDF: {
					newArticle = metadata as PDFArticle;
					break;
				}
				case ArticleType.Image: {
					const imageArticle = metadata as ImageArticle;

					newArticle = imageArticle;
					break;
				}
				case ArticleType.Audio: {
					newArticle = metadata as AudioArticle;
					break;
				}
				case ArticleType.Video: {
					newArticle = metadata as VideoArticle;
					break;
				}
				case ArticleType.Doc: {
					newArticle = metadata as DocArticle;
					break;
				}
				default: {
					console.error('Unknown article type:', metadata.type);
					return undefined;
				}
			}

			if (newArticle) {
				// Set server-assigned ID and metadata
				newArticle.id = externalItem.id;
				newArticle.dateCreated = externalItem.date_added;
				// Add tags from server
				if (externalItem.tags && externalItem.tags.length > 0) {
					newArticle.tags.push(...externalItem.tags);
				}

				// Add to the local articles array
				this.addNewArticle(newArticle);

				return newArticle;
			}
		} catch (error) {
			console.error(
				'Error parsing ExternalItem metadata:',
				externalItem,
				error,
			);
		}

		return undefined;
	}

	/**
	 * Add a new article to the local collection and handle associated resources
	 * @param article The article to add
	 */
	public addNewArticle(article: Article): void {
		// Add to the articles array
		this.articles.push(article);

		// Emit the updated articles array
		this._articlesSubject.next([...this.articles]);

		// Clear any failed fetch cache for this ID
		this.failedFetches.delete(String(article.id));

		// For image files, fetch using GetResizedImage with Thumb size
		if (article.type === 'image') {
			this.backendService
				.GetResizedImage(article.id, Imagesizes.Thumb)
				.pipe(
					tap(() => {
						console.log(
							`Fetched image for newly uploaded article ${article.id} (${article.title}) at size Thumb`,
						);
					}),
					map((data: Blob) => {
						// Convert to file object
						const file = new File([data], article.title, {
							type: data.type,
						});

						console.log(
							`Created file object for image:`,
							article,
							file,
						);

						// Create and store ObjectURL
						const objectURL = URL.createObjectURL(file);
						this.objectURLs.set(article.id, objectURL);
						this.imageSizes.set(article.id, Imagesizes.Thumb);

						return article;
					}),
					catchError((error) => {
						console.error(
							`Error fetching image for newly uploaded article:`,
							article,
							error,
						);
						return of(article);
					}),
				)
				.subscribe();
		} else if (article.type === 'audio') {
			// For audio files, fetch using GetSingleFile
			this.backendService
				.GetSingleFile(article.id)
				.pipe(
					tap(() => {
						console.log(
							`Fetched audio for newly uploaded article ${article.id} (${article.title})`,
						);
					}),
					map((data: Blob) => {
						// Convert to file object
						const file = new File([data], article.title, {
							type: data.type,
						});

						console.log(
							`Created file object for audio:`,
							article,
							file,
						);

						// Create and store ObjectURL
						const objectURL = URL.createObjectURL(file);
						this.objectURLs.set(article.id, objectURL);

						return article;
					}),
					catchError((error) => {
						console.error(
							`Error fetching audio for newly uploaded article:`,
							article,
							error,
						);
						return of(article);
					}),
				)
				.subscribe();
		}

		console.log('Article added to library:', article);
	}
}
